import { AiFillCloseCircle } from "react-icons/ai";
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { format, subDays } from "date-fns";
import { useAdmin } from "../../../context/admin";
import { useGlobal } from "../../../context/global";
import AdminService from "../../../services/admin.service";
import Modal from "../../common/Modal";
import GeneralInput from "../../common/GeneralInput";
import Select from "../../common/Select";
import FileInput from "../../common/FileInput";
import { Agency, CashAccount, CashCategory, CashFlowType, CashPendingPayment, CashShift, MovementTraspassLink, ReservationSearchResult, GroupSummary, TraspassDebt, ExistingPayment, Seller } from "../../../types/common.types";

export default function EditCashMovement() {
    const { setIsLoading } = useGlobal()
    const { editCashMovementData, setEditCashMovementData } = useAdmin()
    const [accounts, setAccounts] = useState<CashAccount[]>([])
    const [categories, setCategories] = useState<CashCategory[]>([])
    const [accountId, setAccountId] = useState(editCashMovementData?.account_id || '')
    const [movementType, setMovementType] = useState<CashFlowType>(editCashMovementData?.movement_type || 'egreso')
    const [amount, setAmount] = useState(String(editCashMovementData?.amount ?? ''))
    const [categoryId, setCategoryId] = useState(editCashMovementData?.category_id || '')
    const [counterpartName, setCounterpartName] = useState(editCashMovementData?.counterpart_name || '')
    const [detail, setDetail] = useState(editCashMovementData?.detail || '')
    const [executionDate, setExecutionDate] = useState(editCashMovementData?.execution_date ? String(editCashMovementData.execution_date).slice(0, 10) : format(new Date(), 'yyyy-MM-dd'))
    const [photo, setPhoto] = useState<File | undefined>()
    const [traspassLinks, setTraspassLinks] = useState<MovementTraspassLink[]>([])
    const [linkAmounts, setLinkAmounts] = useState<Record<string, { tour: number, tickets: number }>>({})
    const [reservationSearch, setReservationSearch] = useState('')
    const [isReservationSearchOpen, setIsReservationSearchOpen] = useState(false)
    const [reservationResults, setReservationResults] = useState<ReservationSearchResult[]>([])
    const [selectedReservation, setSelectedReservation] = useState<ReservationSearchResult | null>(null)
    const [reservationPayments, setReservationPayments] = useState<any[]>([])
    const [paymentReservationId, setPaymentReservationId] = useState('')
    const [ventaAmountTour, setVentaAmountTour] = useState('')
    const [ventaAmountTickets, setVentaAmountTickets] = useState('0')
    const [isGroupMode, setIsGroupMode] = useState(false)
    const [groupSummary, setGroupSummary] = useState<GroupSummary | null>(null)
    // Vincular a un traspaso desde cero — caso real: el vendedor creó el movimiento suelto (sin
    // ningún vínculo todavía) y recién en la revisión semanal se identifica a qué corresponde.
    // Mismo mecanismo que "Nuevo movimiento", pero agregando el primer vínculo en vez de crearlo.
    const [agencies, setAgencies] = useState<Agency[]>([])
    const [traspassAgencyId, setTraspassAgencyId] = useState('')
    const [agencySearch, setAgencySearch] = useState('')
    const [isAgencySearchOpen, setIsAgencySearchOpen] = useState(false)
    const [traspassDebts, setTraspassDebts] = useState<TraspassDebt[]>([])
    const [newSelectedLinks, setNewSelectedLinks] = useState<Record<string, { tour: number, tickets: number }>>({})
    const [debtsFrom, setDebtsFrom] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'))
    const [debtsTo, setDebtsTo] = useState(format(new Date(), 'yyyy-MM-dd'))
    const [existingPaymentSearch, setExistingPaymentSearch] = useState('')
    const [isExistingPaymentSearchOpen, setIsExistingPaymentSearchOpen] = useState(false)
    const [existingPaymentResults, setExistingPaymentResults] = useState<ExistingPayment[]>([])
    const [selectedExistingPayments, setSelectedExistingPayments] = useState<Record<string, ExistingPayment>>({})
    // Abono contra la deuda TOTAL de la agencia (ver comentario en CreateCashMovement.tsx).
    const [isGeneralAbono, setIsGeneralAbono] = useState(false)
    // "Deuda / Anticipo Interno" — ver comentario en CreateCashMovement.tsx.
    const [sellers, setSellers] = useState<Seller[]>([])
    const [advanceSellerId, setAdvanceSellerId] = useState(editCashMovementData?.counterpart_seller_id || '')
    // Turno al que queda asignado este movimiento (opcional) — permite que un movimiento cargado
    // atrasado (ej. corrigiendo un desfase encontrado días después) cuente en el cierre de SU
    // propio turno real, sin importar cuándo se edite. Se recarga con la lista de turnos del día
    // de execution_date, no de "hoy".
    const [shifts, setShifts] = useState<CashShift[]>([])
    const [shiftId, setShiftId] = useState(editCashMovementData?.shift_id || '')
    // Asignar este movimiento (ya registrado) a un pago pendiente — caso real: en la conciliación la
    // línea del banco se vinculó a un movimiento existente, y recién ahora se sabe que era el pago
    // semanal de un aliado. Se muestran los abiertos + el que ya tiene asignado (aunque esté cerrado).
    const [pendingPayments, setPendingPayments] = useState<CashPendingPayment[]>([])
    const [pendingPaymentId, setPendingPaymentId] = useState(editCashMovementData?.pending_payment_id || '')

    useEffect(() => {
        async function loadData() {
            try {
                const [accountsRes, categoriesRes, linksRes, agenciesRes, sellersRes, pendingRes] = await Promise.all([
                    AdminService.listCashAccounts(),
                    AdminService.listCashCategories(),
                    AdminService.listCashMovementTraspassLinks(editCashMovementData!.id!),
                    AdminService.listAgencies(),
                    AdminService.listSellers(),
                    AdminService.listCashPendingPayments()
                ])
                setPendingPayments((pendingRes.data as CashPendingPayment[]).filter(p =>
                    p.status === 'open' || p.id === editCashMovementData?.pending_payment_id
                ))
                setAccounts(accountsRes.data)
                setCategories(categoriesRes.data)
                const links: MovementTraspassLink[] = linksRes.data
                setTraspassLinks(links)
                const initial: Record<string, { tour: number, tickets: number }> = {}
                links.forEach(l => { initial[l.link_id] = { tour: l.amount_tour, tickets: l.amount_tickets } })
                setLinkAmounts(initial)
                setAgencies(agenciesRes.data)
                setSellers(sellersRes.data)
            } catch (error) {
                console.log(error)
            }
        }
        loadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        async function loadShifts() {
            if (!executionDate) { setShifts([]); return }
            try {
                const res = await AdminService.listCashShifts(executionDate, executionDate)
                setShifts(res.data)
            } catch (error) {
                console.log(error)
            }
        }
        loadShifts()
    }, [executionDate])

    const filteredCategories = categories.filter(c => c.flow_type === movementType).sort((a, b) => a.name.localeCompare(b.name))
    const hasTraspassLinks = traspassLinks.length > 0
    const linksTotal = Object.values(linkAmounts).reduce((acc, l) => acc + Number(l.tour || 0) + Number(l.tickets || 0), 0)
    const selectedCategory = categories.find(c => c.id === categoryId)
    // Ver CreateCashMovement: "Ventas Aliados" y cualquier venta asignada a un pago pendiente no se
    // vinculan a un pasajero puntual.
    const isAllyPayment = (selectedCategory?.name || '') === 'Ventas Aliados'
    const isVenta = !hasTraspassLinks && !pendingPaymentId && !isAllyPayment && movementType === 'ingreso' && (selectedCategory?.name || '').startsWith('Ventas')
    const pendingPaymentsForType = pendingPayments.filter(p => (p.category?.flow_type || 'egreso') === movementType)
    const isTraspaso = !hasTraspassLinks && (selectedCategory?.name || '').startsWith('Traspasos')
    const isAdvance = (selectedCategory?.name || '') === 'Deuda / Anticipo Interno'
    const alreadyLinked = !!editCashMovementData?.payment_reservation_id

    function updateLinkAmount(link_id: string, field: 'tour' | 'tickets', value: string) {
        setLinkAmounts(prev => ({ ...prev, [link_id]: { ...prev[link_id], [field]: value === '' ? 0 : Number(value) } }))
    }

    useEffect(() => {
        async function loadDebts() {
            if (!isTraspaso || !traspassAgencyId) { setTraspassDebts([]); return }
            try {
                const res = await AdminService.listTraspassDebts(traspassAgencyId, movementType, debtsFrom, debtsTo)
                setTraspassDebts(res.data)
            } catch (error) {
                console.log(error)
            }
        }
        loadDebts()
        setNewSelectedLinks({})
        setSelectedExistingPayments({})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTraspaso, traspassAgencyId, movementType, debtsFrom, debtsTo])

    useEffect(() => {
        if (!isTraspaso) return
        const q = existingPaymentSearch.trim()
        if (q.length < 2) { setExistingPaymentResults([]); return }
        const timeout = setTimeout(async () => {
            try {
                const res = await AdminService.searchExistingPayments(q)
                setExistingPaymentResults(res.data)
            } catch (error) {
                console.log(error)
            }
        }, 300)
        return () => clearTimeout(timeout)
    }, [isTraspaso, existingPaymentSearch])

    function toggleExistingPayment(payment: ExistingPayment) {
        setSelectedExistingPayments(prev => {
            const next = { ...prev }
            if (next[payment.id]) delete next[payment.id]
            else next[payment.id] = payment
            return next
        })
        setIsExistingPaymentSearchOpen(false)
        setExistingPaymentSearch('')
    }

    const sortedAgencies = useMemo(() => [...agencies].sort((a, b) => a.name.localeCompare(b.name)), [agencies])
    const filteredAgencyOptions = useMemo(
        () => sortedAgencies.filter(a => a.name.toLowerCase().includes(agencySearch.trim().toLowerCase())),
        [sortedAgencies, agencySearch]
    )
    const selectedAgencyName = agencies.find(a => a.id === traspassAgencyId)?.name || ''

    function selectAgency(agency: Agency) {
        setTraspassAgencyId(agency.id)
        setIsAgencySearchOpen(false)
    }

    function toggleNewDebt(debt: TraspassDebt) {
        setNewSelectedLinks(prev => {
            const next = { ...prev }
            if (next[debt.target_id] !== undefined) delete next[debt.target_id]
            else next[debt.target_id] = { tour: debt.tour_remaining, tickets: debt.tickets_remaining }
            return next
        })
    }

    function updateNewLinkAmount(target_id: string, field: 'tour' | 'tickets', value: string) {
        setNewSelectedLinks(prev => ({
            ...prev,
            [target_id]: { ...prev[target_id], [field]: value === '' ? 0 : Number(value) }
        }))
    }

    const newExistingPaymentsSum = Object.values(selectedExistingPayments).reduce((a, p) => a + p.amount + p.amount_tickets, 0)
    const newSelectedLinksSum = Object.values(newSelectedLinks).reduce((a, l) => a + l.tour + l.tickets, 0) + newExistingPaymentsSum

    // Búsqueda de reserva para vincular una Venta ya registrada a su pago real — mismo mecanismo
    // que "Nuevo movimiento", pero aquí sobre un movimiento que el vendedor ya creó suelto.
    useEffect(() => {
        if (!isVenta) return
        const q = reservationSearch.trim()
        if (q.length < 2) { setReservationResults([]); return }
        const timeout = setTimeout(async () => {
            try {
                const res = await AdminService.searchReservations(q)
                setReservationResults(res.data)
            } catch (error) {
                console.log(error)
            }
        }, 300)
        return () => clearTimeout(timeout)
    }, [isVenta, reservationSearch])

    useEffect(() => {
        async function loadPayments() {
            if (!selectedReservation) { setReservationPayments([]); return }
            try {
                const res = isGroupMode && selectedReservation.group_id
                    ? await AdminService.listGroupPayments(selectedReservation.group_id)
                    : await AdminService.listReservationPayments(selectedReservation.id)
                setReservationPayments((res.data || []).filter((p: any) => !p.authorized))
            } catch (error) {
                console.log(error)
            }
        }
        loadPayments()
    }, [selectedReservation, isGroupMode])

    useEffect(() => {
        async function loadGroupSummary() {
            if (!isGroupMode || !selectedReservation?.group_id) { setGroupSummary(null); return }
            try {
                const res = await AdminService.getGroupSummary(selectedReservation.group_id)
                setGroupSummary(res.data)
            } catch (error) {
                console.log(error)
            }
        }
        loadGroupSummary()
    }, [isGroupMode, selectedReservation])

    function selectReservation(r: ReservationSearchResult) {
        setSelectedReservation(r)
        setIsReservationSearchOpen(false)
        setReservationSearch('')
        setPaymentReservationId('')
        setIsGroupMode(false)
        setVentaAmountTour(amount || '')
        setVentaAmountTickets('0')
    }

    async function onSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault()
        if (isVenta && selectedReservation && !paymentReservationId) {
            const sum = Number(ventaAmountTour || 0) + Number(ventaAmountTickets || 0)
            if (Math.abs(sum - Number(amount)) > 0.01) {
                alert('La suma de monto tour + entradas no calza con el monto ingresado')
                return
            }
        }
        const hasNewLinks = Object.keys(newSelectedLinks).length > 0 || Object.keys(selectedExistingPayments).length > 0
        if (isTraspaso && !isGeneralAbono && hasNewLinks && Math.abs(newSelectedLinksSum - Number(amount)) > 0.01) {
            alert('La suma de los pasajeros/traspasos/pagos seleccionados no calza con el monto ingresado')
            return
        }
        setIsLoading(true)
        try {
            const formData = new FormData();
            formData.append('account_id', accountId)
            formData.append('movement_type', movementType)
            formData.append('amount', hasTraspassLinks ? String(linksTotal) : amount)
            formData.append('category_id', categoryId)
            // Siempre se manda (aunque quede vacío al elegir "--") — si no, no hay forma de
            // desasignar un turno ya asignado: el backend conserva el valor anterior cuando el
            // campo no viene en la petición.
            formData.append('shift_id', shiftId)
            const effectiveCounterpartName = isTraspaso && traspassAgencyId
                ? (agencies.find(a => a.id === traspassAgencyId)?.name || counterpartName)
                : isAdvance && advanceSellerId
                    ? (sellers.find(s => s.id === advanceSellerId)?.name || counterpartName)
                    : counterpartName
            formData.append('counterpart_name', effectiveCounterpartName)
            if (isTraspaso && traspassAgencyId) formData.append('counterpart_agency_id', traspassAgencyId)
            if (isAdvance && advanceSellerId) formData.append('counterpart_seller_id', advanceSellerId)
            formData.append('detail', detail)
            if (executionDate) formData.append('execution_date', executionDate)
            if (photo) formData.append('photo', photo)
            // Siempre se manda, igual que shift_id: vacío = quitarle el pago pendiente.
            formData.append('pending_payment_id', pendingPaymentId)
            if (isVenta && selectedReservation) {
                if (!isGroupMode) formData.append('reservation_id', selectedReservation.id)
                if (paymentReservationId) {
                    formData.append('payment_reservation_id', paymentReservationId)
                } else if (isGroupMode && selectedReservation.group_id) {
                    formData.append('new_payment_reservation[group_id]', selectedReservation.group_id)
                    formData.append('new_payment_reservation[amount]', ventaAmountTour || '0')
                    formData.append('new_payment_reservation[amount_tickets]', ventaAmountTickets || '0')
                } else {
                    formData.append('new_payment_reservation[reservation_id]', selectedReservation.id)
                    formData.append('new_payment_reservation[amount]', ventaAmountTour || '0')
                    formData.append('new_payment_reservation[amount_tickets]', ventaAmountTickets || '0')
                }
            }
            if (hasTraspassLinks) {
                // Misma notación de corchetes que Nuevo movimiento (ver comentario ahí): evita que
                // el middleware formidable corrompa un array de objetos.
                traspassLinks.forEach((l, i) => {
                    formData.append(`traspass_links[${i}][link_id]`, l.link_id)
                    formData.append(`traspass_links[${i}][amount_tour]`, String(linkAmounts[l.link_id]?.tour ?? 0))
                    formData.append(`traspass_links[${i}][amount_tickets]`, String(linkAmounts[l.link_id]?.tickets ?? 0))
                })
            } else if (isTraspaso && isGeneralAbono) {
                formData.append('traspass_links[0][is_general]', 'true')
                formData.append('traspass_links[0][amount_tour]', amount)
                formData.append('traspass_links[0][amount_tickets]', '0')
            } else if (isTraspaso && hasNewLinks) {
                let i = 0
                const links = traspassDebts.filter(d => newSelectedLinks[d.target_id] !== undefined)
                links.forEach(d => {
                    const key = d.target_type === 'reservation' ? 'reservation_id' : 're_traspaso_id'
                    formData.append(`traspass_links[${i}][${key}]`, d.target_id)
                    formData.append(`traspass_links[${i}][amount_tour]`, String(newSelectedLinks[d.target_id].tour))
                    formData.append(`traspass_links[${i}][amount_tickets]`, String(newSelectedLinks[d.target_id].tickets))
                    i++
                })
                Object.values(selectedExistingPayments).forEach(p => {
                    formData.append(`traspass_links[${i}][payment_id]`, p.id)
                    formData.append(`traspass_links[${i}][amount_tour]`, String(p.amount))
                    formData.append(`traspass_links[${i}][amount_tickets]`, String(p.amount_tickets))
                    i++
                })
            }
            await AdminService.editCashMovement(editCashMovementData!.id!, formData)
            window.location.reload()
        } catch (error: any) {
            alert(typeof error?.response?.data === 'string' ? error.response.data : 'No se pudo guardar el movimiento')
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Modal onSubmit={onSubmit}>
            <h1>Editar movimiento</h1>
            <div className="input-container">
                <Select
                    label="Tipo"
                    options={[{ value: 'ingreso', name: 'Ingreso' }, { value: 'egreso', name: 'Egreso' }]}
                    value={movementType}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setMovementType(e.target.value as CashFlowType)}
                />
                {!hasTraspassLinks && (
                    <GeneralInput
                        label="Monto"
                        type="number"
                        required
                        value={amount}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
                    />
                )}
            </div>
            <div className="input-container">
                <Select
                    label="Cuenta"
                    defaultNull
                    value={accountId}
                    options={accounts.map(a => ({ value: a.id, name: a.name }))}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setAccountId(e.target.value)}
                />
                <Select
                    label="Categoría"
                    defaultNull
                    value={categoryId}
                    options={filteredCategories.map(c => ({ value: c.id, name: c.name }))}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setCategoryId(e.target.value)}
                />
            </div>
            {(pendingPaymentsForType.length > 0 || pendingPaymentId) && (
                <div className="input-container">
                    <Select
                        label={isAllyPayment ? 'Pago pendiente del aliado' : 'Pago pendiente (opcional)'}
                        defaultNull
                        value={pendingPaymentId}
                        options={pendingPaymentsForType.map(p => ({
                            value: p.id,
                            name: `${p.counterpart_name} — saldo $${(p.total_amount - p.paid_amount).toLocaleString('es-CL')}${p.status === 'closed' ? ' (pagado)' : ''}`
                        }))}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setPendingPaymentId(e.target.value)}
                    />
                </div>
            )}
            {/* Caso real (04/10): David editó el pago de César a "Ventas Aliados" y no apareció
                ningún selector — todavía no había creado el pago pendiente de Lighthouse. */}
            {isAllyPayment && !pendingPaymentId && pendingPaymentsForType.length === 0 && (
                <div className="input-container">
                    <p style={{ margin: 0, fontSize: 13, lineHeight: 1.4, color: '#666' }}>
                        No hay pagos pendientes de aliados abiertos. Créalo primero en Allies → el aliado → Resumen
                        → "Crear pago pendiente", y después vuelve a editar este movimiento para asignarlo.
                    </p>
                </div>
            )}
            <div className="input-container">
                <GeneralInput
                    label="Fecha del movimiento"
                    type="date"
                    required
                    value={executionDate}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setExecutionDate(e.target.value)}
                />
            </div>
            {hasTraspassLinks && (
                <TraspassLinks>
                    <label>Desglose por pasajero/traspaso (el monto total se calcula solo)</label>
                    <ul>
                        {traspassLinks.map(link => (
                            <li key={link.link_id}>
                                <span className="name">{link.passenger_name} — {link.tour_id} ({link.tour_date})</span>
                                <div className="link-amounts">
                                    <label>
                                        Tour
                                        <input
                                            type="number"
                                            min={0}
                                            step="any"
                                            value={linkAmounts[link.link_id]?.tour ?? 0}
                                            onChange={e => updateLinkAmount(link.link_id, 'tour', e.target.value)}
                                        />
                                    </label>
                                    <label>
                                        Entradas
                                        <input
                                            type="number"
                                            min={0}
                                            step="any"
                                            value={linkAmounts[link.link_id]?.tickets ?? 0}
                                            onChange={e => updateLinkAmount(link.link_id, 'tickets', e.target.value)}
                                        />
                                    </label>
                                </div>
                            </li>
                        ))}
                    </ul>
                    <div className="total">Monto total del movimiento: ${linksTotal.toLocaleString('es-CL')}</div>
                </TraspassLinks>
            )}
            {isTraspaso && (
                <NewTraspassLink>
                    <p className="hint">Este movimiento todavía no está vinculado a ningún pasajero/traspaso.</p>
                    <div className="agency-search">
                        <label>Agencia</label>
                        <input
                            type="text"
                            placeholder="Buscar agencia..."
                            value={isAgencySearchOpen ? agencySearch : selectedAgencyName}
                            onFocus={() => { setIsAgencySearchOpen(true); setAgencySearch('') }}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setAgencySearch(e.target.value)}
                            onBlur={() => setTimeout(() => setIsAgencySearchOpen(false), 150)}
                        />
                        {isAgencySearchOpen && (
                            <ul className="agency-options">
                                {filteredAgencyOptions.length === 0 && <li className="empty">Sin resultados</li>}
                                {filteredAgencyOptions.map(a => (
                                    <li key={a.id} onMouseDown={() => selectAgency(a)}>{a.name}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {traspassAgencyId && (
                        <>
                            <label className="general-abono-toggle">
                                <input
                                    type="checkbox"
                                    checked={isGeneralAbono}
                                    onChange={e => setIsGeneralAbono(e.target.checked)}
                                />
                                Es un abono general (no corresponde a un pasajero/traspaso específico — abona contra la deuda total de la agencia)
                            </label>
                            {!isGeneralAbono && (
                            <>
                            <div className="dates-range">
                                <label>
                                    Desde
                                    <input type="date" value={debtsFrom} onChange={e => setDebtsFrom(e.target.value)} />
                                </label>
                                <label>
                                    Hasta
                                    <input type="date" value={debtsTo} onChange={e => setDebtsTo(e.target.value)} />
                                </label>
                            </div>
                            <label>¿A qué pasajero(s)/traspaso(s) corresponde?</label>
                            {traspassDebts.length === 0 && <p className="empty">No hay deudas pendientes con esta agencia en el rango de fechas seleccionado</p>}
                            <ul>
                                {traspassDebts.map(debt => {
                                    const isChecked = newSelectedLinks[debt.target_id] !== undefined
                                    return (
                                        <li key={debt.target_id}>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => toggleNewDebt(debt)}
                                                />
                                                {debt.passenger_name} — {debt.tour_id} ({debt.tour_date}) — saldo tour ${debt.tour_remaining.toLocaleString('es-CL')} / entradas ${debt.tickets_remaining.toLocaleString('es-CL')}
                                            </label>
                                            {isChecked && (
                                                <div className="link-amounts">
                                                    <label>
                                                        Tour
                                                        <input
                                                            className="link-amount"
                                                            type="number"
                                                            min={0}
                                                            max={debt.tour_remaining}
                                                            step="any"
                                                            value={newSelectedLinks[debt.target_id].tour}
                                                            onChange={e => updateNewLinkAmount(debt.target_id, 'tour', e.target.value)}
                                                        />
                                                    </label>
                                                    <label>
                                                        Entradas
                                                        <input
                                                            className="link-amount"
                                                            type="number"
                                                            min={0}
                                                            max={debt.tickets_remaining}
                                                            step="any"
                                                            value={newSelectedLinks[debt.target_id].tickets}
                                                            onChange={e => updateNewLinkAmount(debt.target_id, 'tickets', e.target.value)}
                                                        />
                                                    </label>
                                                </div>
                                            )}
                                        </li>
                                    )
                                })}
                            </ul>
                            <label>¿O ya existe el pago registrado en el sistema?</label>
                            <div className="existing-payment-search">
                                <input
                                    type="text"
                                    placeholder="Buscar pasajero del pago ya registrado..."
                                    value={existingPaymentSearch}
                                    onFocus={() => setIsExistingPaymentSearchOpen(true)}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setExistingPaymentSearch(e.target.value)}
                                    onBlur={() => setTimeout(() => setIsExistingPaymentSearchOpen(false), 150)}
                                />
                                {isExistingPaymentSearchOpen && existingPaymentSearch.trim().length >= 2 && (
                                    <ul className="existing-payment-options">
                                        {existingPaymentResults.length === 0 && <li className="empty">Sin resultados</li>}
                                        {existingPaymentResults.map(p => (
                                            <li key={p.id} onMouseDown={() => toggleExistingPayment(p)}>
                                                {p.passenger_name} — {p.tour_id} ({p.tour_date}) — ${(p.amount + p.amount_tickets).toLocaleString('es-CL')}{p.remarks ? ` — ${p.remarks}` : ''}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            {Object.keys(selectedExistingPayments).length > 0 && (
                                <ul className="selected-existing-payments">
                                    {Object.values(selectedExistingPayments).map(p => (
                                        <li key={p.id}>
                                            <span>{p.passenger_name} — {p.tour_id} ({p.tour_date}) — ${(p.amount + p.amount_tickets).toLocaleString('es-CL')}</span>
                                            <button type="button" onClick={() => toggleExistingPayment(p)}>Quitar</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {(Object.keys(newSelectedLinks).length > 0 || Object.keys(selectedExistingPayments).length > 0) && (
                                <div className="links-summary">
                                    Suma seleccionada: ${newSelectedLinksSum.toLocaleString('es-CL')}
                                    {amount && Math.abs(newSelectedLinksSum - Number(amount)) > 0.01 &&
                                        <span className="warning"> — no calza con el monto ingresado (${Number(amount).toLocaleString('es-CL')})</span>
                                    }
                                </div>
                            )}
                            </>
                            )}
                        </>
                    )}
                </NewTraspassLink>
            )}
            {isVenta && (
                <VentaLink>
                    {alreadyLinked && !selectedReservation && (
                        <p className="already-linked">Este movimiento ya está vinculado a un pago. Busca otra reserva solo si quieres cambiarlo.</p>
                    )}
                    <div className="reservation-search">
                        <label>Vincular a una reserva</label>
                        <input
                            type="text"
                            placeholder="Buscar pasajero..."
                            value={isReservationSearchOpen ? reservationSearch : (selectedReservation ? `${selectedReservation.passenger_name} — ${selectedReservation.tour_id}` : '')}
                            onFocus={() => { setIsReservationSearchOpen(true); setReservationSearch('') }}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setReservationSearch(e.target.value)}
                            onBlur={() => setTimeout(() => setIsReservationSearchOpen(false), 150)}
                        />
                        {isReservationSearchOpen && reservationSearch.trim().length >= 2 && (
                            <ul className="reservation-options">
                                {reservationResults.length === 0 && <li className="empty">Sin resultados</li>}
                                {reservationResults.map(r => (
                                    <li key={r.id} onMouseDown={() => selectReservation(r)}>
                                        {r.passenger_name} — {r.tour_id} ({r.reservation_date}) — saldo ${(r.price + r.tickets - r.paid).toLocaleString('es-CL')}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {selectedReservation && (
                        <>
                            {selectedReservation.group_id && (
                                <label className="group-toggle">
                                    <input
                                        type="checkbox"
                                        checked={isGroupMode}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => setIsGroupMode(e.target.checked)}
                                    />
                                    Este pago corresponde a todo el grupo, no solo a {selectedReservation.passenger_name}
                                </label>
                            )}
                            {isGroupMode && groupSummary ? (
                                <div className="reservation-summary">
                                    Grupo ({groupSummary.passengers.length} pasajero{groupSummary.passengers.length !== 1 ? 's' : ''}): {groupSummary.passengers.map(p => `${p.name} (${p.tour_id})${p.is_original ? '' : ' — agregada después'}`).join(', ')}
                                    <br />
                                    Total: ${(groupSummary.total_price + groupSummary.total_tickets).toLocaleString('es-CL')} · Pagado: ${groupSummary.paid.toLocaleString('es-CL')} · Saldo: ${(groupSummary.total_price + groupSummary.total_tickets - groupSummary.paid).toLocaleString('es-CL')}
                                </div>
                            ) : (
                                <div className="reservation-summary">
                                    Total: ${(selectedReservation.price + selectedReservation.tickets).toLocaleString('es-CL')} · Pagado: ${selectedReservation.paid.toLocaleString('es-CL')} · Saldo: ${(selectedReservation.price + selectedReservation.tickets - selectedReservation.paid).toLocaleString('es-CL')}
                                </div>
                            )}
                            {reservationPayments.length > 0 && (
                                <Select
                                    label="¿Confirmar un pago ya creado (por el vendedor o por ti)?"
                                    value={paymentReservationId}
                                    options={[
                                        { value: '', name: '-- Crear un pago nuevo --' },
                                        ...reservationPayments.map((p: any) => ({
                                            value: p.id,
                                            name: `$${(Number(p.amount) + Number(p.amount_tickets || 0)).toLocaleString('es-CL')} — ${p.remarks || 'sin comentario'}`
                                        }))
                                    ]}
                                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setPaymentReservationId(e.target.value)}
                                    style={{ margin: '10px 0' }}
                                />
                            )}
                            {!paymentReservationId && (
                                <div className="venta-amounts">
                                    <label>
                                        Monto tour
                                        <input type="number" min={0} step="any" value={ventaAmountTour} onChange={e => setVentaAmountTour(e.target.value)} />
                                    </label>
                                    <label>
                                        Monto entradas
                                        <input type="number" min={0} step="any" value={ventaAmountTickets} onChange={e => setVentaAmountTickets(e.target.value)} />
                                    </label>
                                </div>
                            )}
                        </>
                    )}
                </VentaLink>
            )}
            {isAdvance && (
                <div className="input-container">
                    <Select
                        label="Vendedor"
                        required
                        defaultNull
                        value={advanceSellerId}
                        options={sellers.map(s => ({ value: s.id, name: s.name }))}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setAdvanceSellerId(e.target.value)}
                    />
                </div>
            )}
            {/* Solo turnos de vendedor: un turno de Admin es period='FULL' (un mismo turno cubre
                AM y PM), así que asignar un movimiento a uno no puede contar en un cierre
                específico sin arriesgar contarlo dos veces — ver resolveShiftId en el backend. */}
            {shifts.filter(s => s.role === 'seller').length > 0 && (
                <div className="input-container">
                    <Select
                        label="Turno (opcional)"
                        defaultNull
                        value={shiftId}
                        options={shifts.filter(s => s.role === 'seller').map(s => ({
                            value: s.id,
                            name: `Vendedor: ${s.person?.name || ''} (${s.period})`
                        }))}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setShiftId(e.target.value)}
                    />
                </div>
            )}
            <div className="input-container">
                {!isAdvance && (
                    <GeneralInput
                        label="Contraparte"
                        value={counterpartName}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setCounterpartName(e.target.value)}
                    />
                )}
                <GeneralInput
                    label="Detalle"
                    value={detail}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setDetail(e.target.value)}
                />
            </div>
            <FileInput
                label="Foto de respaldo (reemplazar)"
                style={{ marginBottom: '20px' }}
                value={photo}
                deleteFile={() => setPhoto(undefined)}
                accept=".png, .jpg, .jpeg, .heic, .webp"
                onChange={(newFile: File) => setPhoto(newFile)}
            />
            <div className="actions">
                <button>Guardar</button>
            </div>
            <div className="close" onClick={() => setEditCashMovementData(undefined)}>
                <AiFillCloseCircle />
            </div>
        </Modal>
    )
}

const TraspassLinks = styled.div`
margin-bottom: 20px;
padding: 10px;
border-radius: 8px;
background-color: ${props => props.theme.whiteBone};
> label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 8px;
}
ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 220px;
    overflow-y: auto;
}
li {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    .name {
        flex: 1;
        font-size: 13px;
    }
}
.link-amounts {
    display: flex;
    gap: 10px;
    label {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: ${props => props.theme.gray};
    }
    input {
        width: 90px;
        padding: 4px 6px;
        border-radius: 6px;
        border: 1px solid ${props => props.theme.gray};
        font-size: 13px;
    }
}
.total {
    margin-top: 10px;
    font-size: 13px;
    font-weight: 600;
}
`

const NewTraspassLink = styled.div`
margin-bottom: 20px;
padding: 10px;
border-radius: 8px;
background-color: ${props => props.theme.whiteBone};
.hint {
    font-size: 12px;
    color: ${props => props.theme.gray};
    margin-bottom: 10px;
}
> label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    margin: 10px 0 6px;
}
.empty {
    font-size: 13px;
    color: ${props => props.theme.gray};
}
.general-abono-toggle {
    display: flex !important;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 500;
    margin: 10px 0;
    cursor: pointer;
    input {
        width: auto;
        flex-shrink: 0;
    }
}
.agency-search {
    position: relative;
    margin-bottom: 10px;
    label {
        display: block;
        font-size: 13px;
        font-weight: 600;
        margin-bottom: 6px;
    }
    input {
        width: 100%;
        padding: 8px 10px;
        border-radius: 6px;
        border: 1px solid ${props => props.theme.gray};
        font-size: 14px;
        box-sizing: border-box;
    }
    .agency-options {
        position: absolute;
        z-index: 20;
        top: 100%;
        left: 0;
        right: 0;
        max-height: 200px;
        overflow-y: auto;
        list-style: none;
        background: ${props => props.theme.white};
        border: 1px solid ${props => props.theme.gray};
        border-radius: 6px;
        margin-top: 4px;
        box-shadow: 0 3px 8px ${props => props.theme.blackTransparent};
        li {
            padding: 8px 10px;
            font-size: 13px;
            cursor: pointer;
        }
        li:hover {
            background: ${props => props.theme.whiteBone};
        }
        li.empty {
            cursor: default;
            color: ${props => props.theme.gray};
        }
        li.empty:hover {
            background: none;
        }
    }
}
.dates-range {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
    label {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-size: 12px;
        font-weight: 600;
    }
    input {
        padding: 6px;
        border-radius: 6px;
        border: 1px solid ${props => props.theme.gray};
        font-size: 13px;
    }
}
ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 220px;
    overflow-y: auto;
    li {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
    }
    li > label {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        cursor: pointer;
        flex: 1;
    }
    .link-amounts {
        display: flex;
        gap: 10px;
        width: 100%;
        padding-left: 24px;
        label {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
            color: ${props => props.theme.gray};
        }
    }
    .link-amount {
        width: 90px;
        padding: 4px 6px;
        border-radius: 6px;
        border: 1px solid ${props => props.theme.gray};
        font-size: 13px;
    }
}
.existing-payment-search {
    position: relative;
    margin-top: 10px;
    margin-bottom: 10px;
    input {
        width: 100%;
        padding: 8px 10px;
        border-radius: 6px;
        border: 1px solid ${props => props.theme.gray};
        font-size: 14px;
        box-sizing: border-box;
    }
    .existing-payment-options {
        position: absolute;
        z-index: 20;
        top: 100%;
        left: 0;
        right: 0;
        max-height: 200px;
        overflow-y: auto;
        list-style: none;
        background: ${props => props.theme.white};
        border: 1px solid ${props => props.theme.gray};
        border-radius: 6px;
        margin-top: 4px;
        box-shadow: 0 3px 8px ${props => props.theme.blackTransparent};
        li {
            padding: 8px 10px;
            font-size: 13px;
            cursor: pointer;
        }
        li:hover {
            background: ${props => props.theme.whiteBone};
        }
        li.empty {
            cursor: default;
            color: ${props => props.theme.gray};
        }
        li.empty:hover {
            background: none;
        }
    }
}
.selected-existing-payments {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 10px;
    li {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        font-size: 13px;
        background: ${props => props.theme.white};
        border-radius: 6px;
        padding: 6px 10px;
    }
    button {
        border: none;
        background: none;
        color: ${props => props.theme.lightRed};
        cursor: pointer;
        font-size: 12px;
    }
}
.links-summary {
    margin-top: 8px;
    font-size: 13px;
    font-weight: 600;
    .warning {
        color: ${props => props.theme.lightRed};
        font-weight: 400;
    }
}
`

const VentaLink = styled.div`
margin-bottom: 15px;
padding: 10px;
border-radius: 8px;
background-color: ${props => props.theme.whiteBone};
.already-linked {
    font-size: 12px;
    color: ${props => props.theme.gray};
    margin-bottom: 8px;
}
.reservation-search {
    position: relative;
    label {
        display: block;
        font-size: 13px;
        font-weight: 600;
        margin-bottom: 6px;
    }
    input {
        width: 100%;
        padding: 8px 10px;
        border-radius: 6px;
        border: 1px solid ${props => props.theme.gray};
        font-size: 14px;
        box-sizing: border-box;
    }
    .reservation-options {
        position: absolute;
        z-index: 20;
        top: 100%;
        left: 0;
        right: 0;
        max-height: 200px;
        overflow-y: auto;
        list-style: none;
        background: ${props => props.theme.white};
        border: 1px solid ${props => props.theme.gray};
        border-radius: 6px;
        margin-top: 4px;
        box-shadow: 0 3px 8px ${props => props.theme.blackTransparent};
        li {
            padding: 8px 10px;
            font-size: 13px;
            cursor: pointer;
        }
        li:hover {
            background: ${props => props.theme.whiteBone};
        }
        li.empty {
            cursor: default;
            color: ${props => props.theme.gray};
        }
        li.empty:hover {
            background: none;
        }
    }
}
.reservation-summary {
    margin-top: 10px;
    font-size: 13px;
    font-weight: 600;
}
.group-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    font-size: 13px;
    cursor: pointer;
    input {
        cursor: pointer;
    }
}
.venta-amounts {
    display: flex;
    gap: 10px;
    margin-top: 10px;
    label {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-size: 12px;
        font-weight: 600;
        color: ${props => props.theme.gray};
    }
    input {
        padding: 6px;
        border-radius: 6px;
        border: 1px solid ${props => props.theme.gray};
        font-size: 13px;
    }
}
`
